/**
 * Dependencies.
 */
import { FormGroup } from '@prz-dp/services';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

import { UserModel } from '@/api/service-point/user/user.model';
import { usersService } from '@/api/service-point/user/user.service';

/**
 * Component.
 */
@Component({ name: 'users-add-scene' })
export default class extends Vue {
  /**
   * Property `isSaving`.
   */
  isSaving = false;

  /**
   * Property `isFormGroupValid`.
   */
  isFormGroupValid = false;

  /**
   * Property `formGroup`.
   */
  formGroup: FormGroup = null;

  /**
   * Getter `validFormGroup`.
   */
  get validFormGroup() {
    return this.isFormGroupValid;
  }

  /**
   * Getter `notifyErrorSettings`.
   */
  get notifyErrorSettings() {
    return {
      backgroundColor: 'var(--pk-color-error-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-error)',
      iconText: 'warning',
      id: 'deviver-error',
      progressBarColor: 'var(--pk-color-error)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Lifecycle created hook.
   */
  created() {
    this.formGroup = this.$kit.forms.generateFormGroup(
      new UserModel({
        isActive: true
      })
    );
  }

  /**
   * Handle save user.
   */
  async handleSaveUser() {
    this.isFormGroupValid = await this.formGroup.validateForm();

    if (this.isFormGroupValid) {
      try {
        this.isSaving = true;

        await usersService.post(new UserModel(this.formGroup.value));

        this.handleGoBack();
      } catch (error) {
        if (process.env.BUILD_MODE !== 'prd') {
          console.error({
            error,
            method: 'handleSaveUser',
            scene: 'UsersAddScene'
          });
        }

        this.$kit.notify.show(
          this.i18n('SERVICE_POINT.LABELS.ERROR'),
          this.i18n('SERVICE_POINT.ERRORS.SOMETHING_WENT_WRONG'),
          this.notifyErrorSettings
        );
      } finally {
        this.isSaving = false;
      }
    }
  }

  /**
   * Handle get user.
   */
  async handleValidateEmail() {
    const email: string = this.formGroup.get('email').value;
    const isNotInternal = !(email || '').includes('prozis.com');

    if (this.formGroup.get('email').$valid && isNotInternal) {
      try {
        this.isLoading = true;

        const response = await usersService.validateUserEmail({ email });

        this.formGroup = this.$kit.forms.generateFormGroup(
          new UserModel({
            ...response,
            isActive: true
          })
        );
      } catch (error) {
        if (process.env.BUILD_MODE !== 'prd') {
          console.error({
            error,
            method: 'handleValidateEmail',
            scene: 'UsersAddScene'
          });
        }

        this.$kit.notify.show(
          this.i18n('SERVICE_POINT.LABELS.ERROR'),
          this.i18n('SERVICE_POINT.ERRORS.INVALID_USER'),
          this.notifyErrorSettings
        );
      } finally {
        this.isFormGroupValid = await this.formGroup.validateForm();
        this.isLoading = false;
      }
    } else {
      this.$kit.notify.show(
        this.i18n('SERVICE_POINT.LABELS.ERROR'),
        this.i18n('SERVICE_POINT.ERRORS.INVALID_EMAIL'),
        this.notifyErrorSettings
      );
    }
  }

  /**
   * Handle go back.
   */
  handleGoBack() {
    this.$router.back();
  }
}
